import { faBuildings, faChevronDown } from "@fortawesome/pro-regular-svg-icons";
import { Menu, useMediaQuery } from "@mui/material";
import Button from "@mui/material/Button";
import { useTheme } from "@mui/material/styles";
import { useState } from "react";

import { useAccess, useBasicData } from "@/hooks";
import { Permission } from "@/types";

import { Icon } from "../Icon/Icon";
import {
  bindMenu,
  bindMenuTrigger,
  useMenuState,
} from "../Menu/hooks/useMenuState";
import { WorkspaceMenuItem } from "./WorkspaceMenuItem";

interface WorkspaceImporterProps {
  objectOwnerWorkspaceId: string;
  importRequiredPermissions: Permission[];
  importedWorkspaces: string[];
  onImport: (workspaceId: string) => Promise<void>;
  onUnimport: (workspaceId: string) => Promise<void>;
}

export const WorkspaceImporter = (props: WorkspaceImporterProps) => {
  const {
    objectOwnerWorkspaceId,
    importRequiredPermissions,
    importedWorkspaces,
    onImport,
    onUnimport,
  } = props;

  const { userWorkspaces } = useBasicData();
  const { hasPermissionInWorkspace } = useAccess();

  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("lg"));

  const menuState = useMenuState({
    openOnClick: true,
  });

  const [busyWorkspaces, setBusyWorkspaces] = useState<Record<string, boolean>>(
    {},
  );

  if (userWorkspaces.length === 0) {
    return null;
  }

  const handleImport = async (workspaceId: string) => {
    setBusyWorkspaces((prev) => ({
      ...prev,
      [workspaceId]: true,
    }));

    try {
      await onImport(workspaceId);
    } finally {
      setBusyWorkspaces((prev) => ({
        ...prev,
        [workspaceId]: false,
      }));
    }
  };

  const handleUnimport = async (workspaceId: string) => {
    setBusyWorkspaces((prev) => ({
      ...prev,
      [workspaceId]: true,
    }));

    try {
      await onUnimport(workspaceId);
    } finally {
      setBusyWorkspaces((prev) => ({
        ...prev,
        [workspaceId]: false,
      }));
    }
  };

  return (
    <>
      <Button
        color="secondary"
        variant="contained"
        size="small"
        startIcon={
          isNarrow ? undefined : (
            <Icon icon={faBuildings} style={{ marginLeft: "4px" }} />
          )
        }
        endIcon={<Icon icon={faChevronDown} />}
        {...bindMenuTrigger(menuState)}
        title={isNarrow ? "Import to Workspaces" : ""}
        aria-label="Import to Workspaces"
      >
        {isNarrow ? <Icon icon={faBuildings} /> : "Import to Workspaces"}
      </Button>

      <Menu
        aria-label="Select the workspaces to import to"
        disableScrollLock={true}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{
          "& .MuiPaper-root": {
            minWidth: "120px",
          },
        }}
        {...bindMenu(menuState)}
        onClose={menuState.onClose}
      >
        {userWorkspaces.map((userWorkspace) => {
          const allowImport = importRequiredPermissions.every((p) =>
            hasPermissionInWorkspace(p, userWorkspace.id),
          );

          const isBusy = busyWorkspaces[userWorkspace.id] ?? false;

          const isImported = importedWorkspaces.includes(userWorkspace.id);

          return (
            <WorkspaceMenuItem
              key={userWorkspace.id}
              workspace={userWorkspace}
              objectOwnerWorkspaceId={objectOwnerWorkspaceId}
              allowImport={allowImport}
              isLoading={isBusy}
              isImported={isImported}
              onUnimport={handleUnimport}
              onImport={handleImport}
            />
          );
        })}
      </Menu>
    </>
  );
};
